import { format } from "date-fns";

import { SyncRequestsBoolExp } from "src/graphql";
import { isEnum } from "src/types/utils";

export enum SyncRunsFilter {
  ALL_RUNS = "all_runs",
  NO_OPERATIONS = "no_operations",
  NO_SUCCESSFUL_OPERATIONS = "no_successful_operations",
}

export type ThresholdComparator = "any" | "over" | "under";

export type RowResultMetric = "rejected" | "successful";

/**
 * Query parameters holding the runs-page filter state. Values arrive from the
 * URL, so the parsers below treat anything malformed as the filter's default.
 */
export const SYNC_RUNS_FILTER_PARAMS = [
  "view",
  "startedAfter",
  "startedBefore",
  "operations",
  "operationsValue",
  "rowsMetric",
  "rows",
  "rowsValue",
] as const;

// A missing parameter can surface as null (URLSearchParams.get) or undefined
// (useQueryState with no default), so every parser accepts both.
type ParamValue = string | null | undefined;

export const parseSyncRunsView = (value: ParamValue): SyncRunsFilter =>
  isEnum(SyncRunsFilter)(value) ? value : SyncRunsFilter.ALL_RUNS;

export const parseThresholdComparator = (
  value: ParamValue,
): ThresholdComparator =>
  value === "over" || value === "under" ? value : "any";

export const parseRowResultMetric = (value: ParamValue): RowResultMetric =>
  value === "successful" ? "successful" : "rejected";

export const parseFilterDate = (value: ParamValue): Date | undefined => {
  if (!value) {
    return undefined;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

export const parseThreshold = (value: ParamValue): number | undefined => {
  if (!value || value.trim() === "") {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
};

/** Formats a date as the local-time string a datetime-local input expects. */
export const formatDateTimeLocal = (
  date: Date | undefined,
): string | undefined =>
  date === undefined ? undefined : format(date, "yyyy-MM-dd'T'HH:mm");

export type SyncRunsFilterState = {
  runsFilter: SyncRunsFilter;
  /**
   * Inclusive created_at bounds, used exactly as given — the filter UI is
   * responsible for defaulting a date-only pick to the start/end of that day.
   */
  rangeStart?: Date;
  rangeEnd?: Date;
  operationsComparator: ThresholdComparator;
  operationsThreshold?: number;
  rowResultMetric: RowResultMetric;
  rowResultComparator: ThresholdComparator;
  /** Percent, 0-100. */
  rowResultThreshold?: number;
};

// rejected_ratio is rounded to 4 decimal places server-side; matching that
// here keeps boundary comparisons (e.g. "successful over 70" vs exactly 70%)
// from wobbling on float error like 1 - 0.7 = 0.30000000000000004.
const toRatio = (percent: number): number => Math.round(percent * 100) / 10000;

const getViewFilter = (type: SyncRunsFilter): SyncRequestsBoolExp => {
  switch (type) {
    case SyncRunsFilter.NO_OPERATIONS:
      return {
        computed_progress: {
          _not: {
            add_attempted_count: { _eq: "0" },
            change_attempted_count: { _eq: "0" },
            remove_attempted_count: { _eq: "0" },
          },
        },
      };
    case SyncRunsFilter.NO_SUCCESSFUL_OPERATIONS:
      return {
        sync_attempts: { successful_operations_count: { _gt: 0 } },
      };
    default:
      // Get all attempts if they exist or not
      return {};
  }
};

export const buildSyncRunsHasuraFilter = (
  syncId: string,
  {
    runsFilter,
    rangeStart,
    rangeEnd,
    operationsComparator,
    operationsThreshold,
    rowResultMetric,
    rowResultComparator,
    rowResultThreshold,
  }: SyncRunsFilterState,
): SyncRequestsBoolExp => {
  const conditions: SyncRequestsBoolExp[] = [];

  if (rangeStart) {
    conditions.push({ created_at: { _gte: rangeStart.toISOString() } });
  }
  if (rangeEnd) {
    conditions.push({ created_at: { _lte: rangeEnd.toISOString() } });
  }

  if (operationsComparator !== "any" && operationsThreshold !== undefined) {
    conditions.push({
      total_operations:
        operationsComparator === "over"
          ? { _gt: operationsThreshold }
          : { _lt: operationsThreshold },
    });
  }

  if (rowResultComparator !== "any" && rowResultThreshold !== undefined) {
    // Successful % is the complement of rejected %, so "successful over X" is
    // expressed as "rejected under 100 - X" against the one computed field.
    // Runs with no operations have a null ratio and are excluded from
    // percentage filters either way.
    const filterOnRejected =
      rowResultMetric === "rejected"
        ? {
            comparator: rowResultComparator,
            ratio: toRatio(rowResultThreshold),
          }
        : {
            comparator: rowResultComparator === "over" ? "under" : "over",
            ratio: toRatio(100 - rowResultThreshold),
          };
    conditions.push({
      rejected_ratio:
        filterOnRejected.comparator === "over"
          ? { _gt: filterOnRejected.ratio }
          : { _lt: filterOnRejected.ratio },
    });
  }

  return {
    destination_instance_id: { _eq: syncId },
    ...getViewFilter(runsFilter),
    ...(conditions.length > 0 ? { _and: conditions } : {}),
  };
};

/**
 * True when the filter contains a predicate on a computed field
 * (total_operations / rejected_ratio), mirroring buildSyncRunsHasuraFilter:
 * a comparator only becomes a predicate once its threshold is set. Computed
 * fields are backed by SQL functions Postgres evaluates per row, so callers
 * use this to skip work that scans a sync's entire run history (e.g. the
 * pagination count, which has no early exit the way a limited page does).
 */
export const hasComputedFieldPredicate = ({
  operationsComparator,
  operationsThreshold,
  rowResultComparator,
  rowResultThreshold,
}: SyncRunsFilterState): boolean =>
  (operationsComparator !== "any" && operationsThreshold !== undefined) ||
  (rowResultComparator !== "any" && rowResultThreshold !== undefined);
